<script setup lang="ts">
import { type Category, CategoryType } from "mws-ts-v2-sdk";

import { useScreenSize } from "~/composables/useScreenSize";

const { category } = defineProps<{
  category?: Category;
  hideBanner?: boolean;
}>();

const imgLoadError = ref(false);

const { isMobile } = useScreenSize();
const featureToggles = useFeatureToggles();

const bannerPreset = computed(() => {
  if (isMobile.value) return "categoryBannerMobile";

  return "categoryBannerDesktop";
});

const categoryType = computed(() => category?.externalReference?.type);

const platformStore = usePlatformStore();
</script>

<template>
  <BaseLoadingSpinner v-if="!category" />
  <div
    v-else
    class="block-container category-header-block"
  >
    <div
      class="site-viewport-container category-header-block__top"
      :class="{ 'pb-6': !!category.bannerUrl }"
    >
      <NuxtImg
        v-if="category.logoUrl"
        :src="category.logoUrl"
        :alt="`${category.name} logo`"
        provider="imgix"
        preset="logoMedium"
        class="category-header-block__top__logo"
      />

      <div class="category-header-block__top__title">
        <BaseTitle as="h1">
          {{ category.name }}
        </BaseTitle>

        <span class="category-header-block__top__info">
          <CategoryFollowButton
            v-if="
              featureToggles.customerPreferences &&
              (platformStore.isFrameTheGame ||
                categoryType !== CategoryType.Other)
            "
            :id="category.id"
            source="categoryPage"
            :slug="category.slug"
            data-test="category-follow-button"
            :name="category.name"
            :is-followed-by-user="category.isFollowedByUser ?? false"
            :type="category.externalReference?.type"
          />
        </span>
      </div>
    </div>
    <div
      v-if="category.bannerUrl && !hideBanner && !imgLoadError"
      class="browser-viewport-span md:site-viewport-container category-header-block__banner__container"
    >
      <div
        v-if="category.primaryColor"
        class="category-header-block__banner__left-ribbon"
        :style="`background-color: ${category.primaryColor};`"
      />

      <NuxtImg
        provider="imgix"
        :alt="`${category.name} header image`"
        sizes="sm:100vw md:100vw lg:100vw xl:100vw 2xl:100vw 3xl:100vw"
        :preset="bannerPreset"
        :src="category.bannerUrl"
        class="category-header-block__banner__image"
        placeholder-class="animate-pulse"
        :placeholder="true"
        @error="imgLoadError = true"
      />
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.row {
  @apply flex flex-row items-center;
}
.category-header-block {
  @apply pt-6;

  &__banner {
    &__container {
      @apply relative;
      @apply aspect-[2100/1480] md:aspect-[2100/800];
    }

    &__left-ribbon {
      @apply bg-platform-color-1 h-full absolute w-4 left-0 md:-left-4;
    }

    &__image {
      @apply object-cover w-full h-full;
    }
  }

  &__top {
    @apply flex justify-start items-center gap-x-4;

    &__logo {
      @apply h-14 lg:h-16 aspect-square object-contain;
    }

    &__title {
      @apply flex flex-1 gap-1 md:gap-4 justify-between flex-row items-center;

      &__total-raised {
        @apply flex flex-row gap-1 text-2xl md:text-base ml-auto;
      }
    }

    &__info {
      @apply flex gap-4;
    }
  }
}
</style>
